import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, UserCheck, UserX, LogOut, Trash2, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PasswordGate } from "@/components/PasswordGate";
import { Badge } from "@/components/ui/badge";

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="border border-white/10 bg-[#0D0D0D] p-8" data-testid={testId}>
    <Icon className="mb-6 h-5 w-5 text-gold" strokeWidth={1.25} />
    <p className="font-display text-5xl font-light text-[#F5F5F0]" data-testid={`${testId}-value`}>{value}</p>
    <p className="mt-3 text-xs uppercase tracking-[0.25em] text-[#666666]">{label}</p>
  </div>
);

export default function AdminPage() {
  const [auth, setAuth] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/rsvps");
      setRsvps(data);
    } catch {
      toast.error("Could not load RSVPs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/auth/admin/me")
      .then(() => {
        setAuth(true);
        load();
      })
      .catch(() => setAuth(false));
  }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/rsvps/${id}`);
      setRsvps((prev) => prev.filter((r) => r.id !== id));
      toast.success("RSVP removed.");
    } catch {
      toast.error("Could not remove RSVP.");
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setAuth(false);
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [
      ["Name", "Status", "Guests", "Dietary / Message", "Replied"],
      ...rsvps.map((r) => [
        r.name,
        r.status,
        r.status === "accepted" ? r.guests : "",
        r.notes,
        new Date(r.updated_at).toLocaleString(),
      ]),
    ];
    const csv = rows.map((row) => row.map(esc).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "lindsays-60th-guest-list.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Guest list downloaded.");
  };

  if (auth === null) {
    return <div className="min-h-screen bg-[#050505]" data-testid="admin-loading" />;
  }

  if (!auth) {
    return (
      <PasswordGate
        overline="Organiser only"
        title={<>The <em className="text-gold">guest list</em>.</>}
        subtitle="This page is for the party organisers. Enter the organiser password to see who has accepted or declined."
        testIdPrefix="admin"
        onSubmit={async (password) => {
          await api.post("/auth/admin", { password });
          setAuth(true);
          load();
        }}
      />
    );
  }

  const accepted = rsvps.filter((r) => r.status === "accepted");
  const declined = rsvps.filter((r) => r.status === "declined");
  const totalGuests = accepted.reduce((sum, r) => sum + r.guests, 0);

  return (
    <main className="min-h-screen bg-[#050505] px-6 py-16 md:px-16 lg:px-24" data-testid="admin-dashboard">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
        <div className="mb-6 flex items-center gap-6">
          <span className="font-display text-sm italic text-gold">Organiser</span>
          <span className="gold-rule flex-1" aria-hidden="true" />
        </div>
        <div className="mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <h1 className="font-display text-4xl font-light tracking-tight text-[#F5F5F0] md:text-6xl">
            Who's <em className="text-gold">coming</em>
          </h1>
          <div className="flex gap-4">
            <button
              data-testid="admin-export-button"
              onClick={exportCsv}
              className="flex items-center gap-2 border border-white/15 px-5 py-3 text-xs uppercase tracking-[0.2em] text-[#A1A19A] transition-colors duration-300 hover:border-gold/40 hover:text-gold"
            >
              <Download className="h-3.5 w-3.5" strokeWidth={1.5} />
              Export
            </button>
            <button
              data-testid="admin-refresh-button"
              onClick={load}
              className="flex items-center gap-2 border border-white/15 px-5 py-3 text-xs uppercase tracking-[0.2em] text-[#A1A19A] transition-colors duration-300 hover:border-gold/40 hover:text-gold"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
              Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 border border-white/15 px-5 py-3 text-xs uppercase tracking-[0.2em] text-[#A1A19A] transition-colors duration-300 hover:border-gold/40 hover:text-gold"
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} />
              Log out
            </button>
          </div>
        </div>

        <div className="mb-16 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={Users} label="Responses" value={rsvps.length} testId="admin-stats-total" />
          <StatCard icon={UserCheck} label="Accepted" value={accepted.length} testId="admin-stats-accepted" />
          <StatCard icon={UserX} label="Declined" value={declined.length} testId="admin-stats-declined" />
          <StatCard icon={Users} label="Seats needed" value={totalGuests} testId="admin-stats-guests" />
        </div>

        <div className="border border-white/10" data-testid="admin-rsvp-table">
          <div className="hidden grid-cols-12 gap-4 border-b border-white/10 bg-[#0D0D0D] px-8 py-4 text-[10px] uppercase tracking-[0.25em] text-[#666666] md:grid">
            <span className="col-span-3">Name</span>
            <span className="col-span-2">Status</span>
            <span className="col-span-1">Guests</span>
            <span className="col-span-4">Dietary / message</span>
            <span className="col-span-1">Replied</span>
            <span className="col-span-1 text-right">Remove</span>
          </div>
          {rsvps.length === 0 && (
            <p className="px-8 py-16 text-sm text-[#666666]" data-testid="admin-rsvp-empty">
              No responses yet. The invitations have only just landed.
            </p>
          )}
          {rsvps.map((r) => (
            <div
              key={r.id}
              data-testid={`admin-rsvp-row-${r.id}`}
              className="grid grid-cols-1 gap-3 border-b border-white/5 px-8 py-5 text-sm transition-colors duration-200 hover:bg-white/[0.03] md:grid-cols-12 md:items-center md:gap-4"
            >
              <span className="col-span-3 font-medium text-[#F5F5F0]">{r.name}</span>
              <span className="col-span-2">
                <Badge
                  data-testid={`admin-rsvp-status-${r.id}`}
                  className={`rounded-none border px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${
                    r.status === "accepted"
                      ? "border-gold/40 bg-gold/10 text-gold"
                      : "border-white/15 bg-white/5 text-[#A1A19A]"
                  }`}
                >
                  {r.status}
                </Badge>
              </span>
              <span className="col-span-1 text-[#A1A19A]">{r.status === "accepted" ? r.guests : "—"}</span>
              <span className="col-span-4 text-[#A1A19A]">{r.notes || "—"}</span>
              <span className="col-span-1 text-xs text-[#666666]">
                {new Date(r.updated_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
              </span>
              <span className="col-span-1 md:text-right">
                <button
                  data-testid={`admin-rsvp-delete-${r.id}`}
                  onClick={() => remove(r.id)}
                  className="text-[#666666] transition-colors duration-200 hover:text-[#E07070]"
                  aria-label={`Remove ${r.name}`}
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </span>
            </div>
          ))}
        </div>

        {declined.some((r) => r.notes) && (
          <div className="mt-16" data-testid="admin-lindsay-messages">
            <h2 className="font-display text-2xl font-light text-[#F5F5F0] md:text-3xl">
              For <em className="text-gold">Lindsay</em> — after the party
            </h2>
            <p className="mt-3 text-sm text-[#666666]">
              Messages from guests who can't make it. Save these to share with her afterwards.
            </p>
            <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {declined.filter((r) => r.notes).map((r) => (
                <div key={r.id} data-testid={`admin-message-${r.id}`} className="border border-white/10 bg-[#0D0D0D] p-8">
                  <p className="font-display text-lg font-light italic leading-relaxed text-[#F5F5F0]">“{r.notes}”</p>
                  <p className="mt-5 text-xs uppercase tracking-[0.25em] text-gold">{r.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="mt-10 text-xs text-[#666666]">
          <a href="/" data-testid="admin-back-link" className="border-b border-white/15 pb-0.5 transition-colors duration-200 hover:text-gold">
            Back to the invitation
          </a>
        </p>
      </motion.div>
    </main>
  );
}
